// 파일 **별도 창** 규약(설계서 §9.4 "파일 창", 2026-09-29) — 세션 창(host) ↔ 파일 창(win) 사이에 오가는 메시지.
//
// 🔴 진실원은 **세션 창 하나**다(채팅 창과 같은 원칙). 파일 채널(DataChannel)·두 칸의 목록·전송은 세션 창의
//    `useFilePanel`·`useFileTransfer` 에 있고, 파일 창은 그 상태를 받아 그리고 사람의 동작을 되돌려 보낼 뿐이다.
//
// 예외 둘 — **브라우저가 사용자 클릭이 일어난 창에서만 여는 선택 창**:
//   - [📂 폴더 열기](내 PC 칸): 파일 창이 폴더를 고르고 **폴더 핸들**을 세션 창에 넘긴다(`localDir`).
//   - [파일 보내기…]·[폴더 보내기…]: 파일 창이 고르고 **File 목록**을 넘긴다(`sendPicked`).
//   둘 다 브라우저 `postMessage`(구조화 복제)로만 실린다. 앱(통합앱)은 이 둘을 쓰지 않는다 — 내 PC 칸을 네이티브가
//   홈부터 스스로 열고(`opensLocalOnItsOwn`), 거기서 골라 [보내기 →] 하면 된다(Tauri 이벤트는 JSON 만 싣는다).

// 🔴 값 import 는 **`.ts` 확장자·순수 모듈만** — 단위 시험(`node --test`)이 이 파일을 바로 불러 쓴다(타입 import 는 지워진다).
import { newPopupKey } from '../popup/popupKey.ts';
import { SELECT_MODES, type SelectMode } from './panelView.ts';
import type { PopupSpec } from '../popup/windowLink';
import type { PickedFile } from './source';
import type { FilePanel, PanelSide, Side } from './useFilePanel';
import type { TransferView } from './useFileTransfer';

/**
 * 파일 창 종류 값. 🔴 src-tauri `popup_window.rs` 의 `FILES` 와 같아야 한다
 * (이벤트 이름 — Rust 가 `bye` 를 `evtToHost` 로 보낸다 · 명령 이름 · 전역 이름 · 크기).
 */
export const FILE_WINDOW_SPEC: PopupSpec = {
  page: 'files.html',
  tag: 'rsFiles',
  browserPrefix: 'rs-files-',
  browserFeatures: 'popup,width=900,height=560',
  evtToHost: 'rs-filewin-to-host',
  evtToWin: 'rs-filewin-to-win',
  openCommand: 'open_file_window',
  closeCommand: 'close_file_window',
  initGlobal: '__RS_FILES__',
};

/** 파일 창에 보이는 알림(버튼 없는 모양 — 버튼 달린 알림은 세션 창 도구 막대에만 뜬다). */
export interface WireNotice {
  kind: 'ok' | 'err';
  text: string;
}

/** 파일 창이 그릴 상태. */
export interface FileViewState {
  title: string;
  local: PanelSide;
  remote: PanelSide;
  canPickLocal: boolean;
  canSendPicked: boolean;
  busy: boolean;
  transfer: TransferView | null;
  notice: WireNotice | null;
}

/** 두 칸 공통 동작(이름만 넘긴다 — 실제 일은 세션 창의 `useFilePanel` 이 한다). */
export type PanelOp =
  | { op: 'openDir'; side: Side; name: string }
  | { op: 'up'; side: Side }
  | { op: 'refresh'; side: Side }
  | { op: 'select'; side: Side; name: string; mode: SelectMode }
  | { op: 'clearSelection'; side: Side }
  /** 칸 머리 드라이브 목록에서 고름 — 세션 창이 자기 `roots` 에 있는지 다시 본다. */
  | { op: 'openRoot'; side: Side; root: string }
  | { op: 'mkdir'; side: Side; name: string }
  | { op: 'remove'; side: Side }
  | { op: 'upload' }
  | { op: 'download' };

export type HostToWin =
  | { t: 'state'; s: FileViewState }
  /** 세션이 끝났다 — 창은 곧 닫힌다. */
  | { t: 'end' };

export type WinToHost =
  /** 창이 떴다 — 전체 상태를 다시 보내 달라(그 전에 보낸 것은 리스너가 없어 사라졌다). */
  | { t: 'hello' }
  | { t: 'op'; o: PanelOp }
  /** 브라우저 전용: 파일 창에서 고른 내 PC 폴더. */
  | { t: 'localDir'; handle: FileSystemDirectoryHandle }
  /** 브라우저 전용: 파일 창에서 고른 파일들 → 원격의 연 폴더로. */
  | { t: 'sendPicked'; picked: PickedFile[]; folder: boolean }
  | { t: 'cancel' }
  | { t: 'dismissNotice' }
  /** 파일 화면을 닫는다(창의 [닫기]). */
  | { t: 'close' }
  /** 창이 사라졌다 — 앱은 Rust 가, 브라우저는 세션 창의 감시가 만든다(창 스스로는 보내지 않는다). */
  | { t: 'bye' };

const WIN_TO_HOST = new Set(['hello', 'op', 'localDir', 'sendPicked', 'cancel', 'dismissNotice', 'close', 'bye']);
const HOST_TO_WIN = new Set(['state', 'end']);
const SIDES = new Set(['local', 'remote']);
const OPS = new Set(['openDir', 'up', 'refresh', 'select', 'clearSelection', 'openRoot', 'mkdir', 'remove', 'upload', 'download']);
const MODES = new Set<string>(SELECT_MODES);

/** 받은 값이 파일 창 메시지인가(모양만 — 이름 규칙은 세션 창의 채널·원격이 본다). */
export function isWinToHost(v: unknown): v is WinToHost {
  if (!v || typeof v !== 'object' || !WIN_TO_HOST.has((v as { t?: unknown }).t as string)) {
    return false;
  }
  const m = v as { t: string; o?: { op?: unknown; side?: unknown; name?: unknown; mode?: unknown; root?: unknown } };
  if (m.t !== 'op') {
    return true;
  }
  const o = m.o;
  if (!o || !OPS.has(o.op as string)) {
    return false;
  }
  // 칸이 필요한 동작은 칸 이름을, 이름이 필요한 동작은 문자열 이름을 가져야 한다.
  const needsSide = o.op !== 'upload' && o.op !== 'download';
  const needsName = o.op === 'openDir' || o.op === 'select' || o.op === 'mkdir';
  const needsMode = o.op === 'select';
  return (
    (!needsSide || SIDES.has(o.side as string)) &&
    (!needsName || typeof o.name === 'string') &&
    (!needsMode || MODES.has(o.mode as string)) &&
    (o.op !== 'openRoot' || typeof o.root === 'string')
  );
}

export function isHostToWin(v: unknown): v is HostToWin {
  return !!v && typeof v === 'object' && HOST_TO_WIN.has((v as { t?: unknown }).t as string);
}

/** 세션 키(창 label 에 들어간다 — Rust `valid_key` 규칙). */
export function newFileWindowKey(): string {
  return newPopupKey('f');
}

/** 두 칸 동작을 세션 창의 패널에 그대로 건다(파일 창은 이름만 넘긴다). */
export function runPanelOp(panel: FilePanel, o: PanelOp): void {
  switch (o.op) {
    case 'openDir':
      panel.openDir(o.side, o.name);
      break;
    case 'up':
      panel.up(o.side);
      break;
    case 'refresh':
      panel.refresh(o.side);
      break;
    case 'select':
      panel.select(o.side, o.name, o.mode);
      break;
    case 'clearSelection':
      panel.clearSelection(o.side);
      break;
    case 'openRoot':
      panel.openRoot(o.side, o.root);
      break;
    case 'mkdir':
      panel.mkdir(o.side, o.name);
      break;
    case 'remove':
      panel.remove(o.side);
      break;
    case 'upload':
      panel.upload();
      break;
    case 'download':
      panel.download();
      break;
  }
}
